/**
 * Campaign Store — Pure logic for campaign inventory management.
 * No DOM, no Three.js.
 */
import type { CampaignInventory, CampaignState, WeaponKey, PlayerLoadout, ItemKey, PlayerState } from '../types';
import { CAMPAIGN_WEAPON_PRICES, WEAPONS, HEAL_ITEMS, UPGRADE_AP_COST, UPGRADE_SKILL_COST, UPGRADE_FITNESS_COST, UPGRADE_JUMP_COST, SKILL_PROGRESSION, MAX_UPGRADE_LEVEL, MAX_AP_LEVEL } from '../constants';
import { upgradeUnitAp, upgradeUnitSkill, upgradeUnitFitness, upgradeUnitJump } from './campaignRoster';
import { createDefaultGearState, isItemUnlocked } from './campaignGear';

// =============================================================================
// DEFAULT INVENTORY
// =============================================================================

export function createDefaultInventory(): CampaignInventory {
  return {
    ownedWeapons: new Set<WeaponKey>(['standard', 'probe']),
    ammoStock: {},
    items: {},
    gearUpgrades: createDefaultGearState(),
  };
}

// =============================================================================
// PURCHASES
// =============================================================================

export function purchaseWeapon(
  state: CampaignState,
  weapon: WeaponKey
): CampaignState | null {
  const price = CAMPAIGN_WEAPON_PRICES[weapon];
  if (price === undefined) return null; // standard/probe not purchasable
  if (state.inventory.ownedWeapons.has(weapon)) return null;
  if (state.playerMoney < price) return null;

  const newOwned = new Set(state.inventory.ownedWeapons);
  newOwned.add(weapon);

  return {
    ...state,
    playerMoney: state.playerMoney - price,
    inventory: {
      ...state.inventory,
      ownedWeapons: newOwned,
    },
  };
}

export function purchaseAmmo(
  state: CampaignState,
  weapon: WeaponKey,
  qty: number
): CampaignState | null {
  if (!state.inventory.ownedWeapons.has(weapon)) return null;
  const costPer = WEAPONS[weapon].cbpCost;
  const totalCost = costPer * qty;
  if (state.playerMoney < totalCost) return null;

  const currentStock = state.inventory.ammoStock[weapon] ?? 0;
  const newAmmoStock = { ...state.inventory.ammoStock, [weapon]: currentStock + qty };

  return {
    ...state,
    playerMoney: state.playerMoney - totalCost,
    inventory: {
      ...state.inventory,
      ammoStock: newAmmoStock,
    },
  };
}

export function purchaseItem(
  state: CampaignState,
  item: ItemKey,
  qty: number
): CampaignState | null {
  if (!isItemUnlocked(state.inventory.gearUpgrades, item)) return null;
  const def = HEAL_ITEMS[item];
  const totalCost = def.cbpCost * qty;
  if (state.playerMoney < totalCost) return null;

  const currentStock = state.inventory.items[item] ?? 0;
  const newItems = { ...state.inventory.items, [item]: currentStock + qty };

  return {
    ...state,
    playerMoney: state.playerMoney - totalCost,
    inventory: {
      ...state.inventory,
      items: newItems,
    },
  };
}

// =============================================================================
// UNIT UPGRADES
// =============================================================================

export function purchaseApUpgrade(
  state: CampaignState,
  unitId: number
): CampaignState | null {
  const unit = state.roster.find(u => u.id === unitId);
  if (!unit) return null;
  if (unit.maxAp >= MAX_AP_LEVEL) return null;
  if (state.playerMoney < UPGRADE_AP_COST) return null;
  return {
    ...state,
    playerMoney: state.playerMoney - UPGRADE_AP_COST,
    roster: upgradeUnitAp(state.roster, unitId),
  };
}

export function purchaseSkillUpgrade(
  state: CampaignState,
  unitId: number
): CampaignState | null {
  const unit = state.roster.find(u => u.id === unitId);
  if (!unit) return null;
  const idx = SKILL_PROGRESSION.indexOf(unit.skillKey);
  if (idx < 0 || idx >= SKILL_PROGRESSION.length - 1) return null;
  if (state.playerMoney < UPGRADE_SKILL_COST) return null;
  return {
    ...state,
    playerMoney: state.playerMoney - UPGRADE_SKILL_COST,
    roster: upgradeUnitSkill(state.roster, unitId),
  };
}

export function purchaseFitnessUpgrade(
  state: CampaignState,
  unitId: number
): CampaignState | null {
  const unit = state.roster.find(u => u.id === unitId);
  if (!unit) return null;
  if ((unit.fitnessLevel ?? 0) >= MAX_UPGRADE_LEVEL) return null;
  if (state.playerMoney < UPGRADE_FITNESS_COST) return null;
  return {
    ...state,
    playerMoney: state.playerMoney - UPGRADE_FITNESS_COST,
    roster: upgradeUnitFitness(state.roster, unitId),
  };
}

export function purchaseJumpUpgrade(
  state: CampaignState,
  unitId: number
): CampaignState | null {
  const unit = state.roster.find(u => u.id === unitId);
  if (!unit) return null;
  if ((unit.jumpLevel ?? 0) >= MAX_UPGRADE_LEVEL) return null;
  if (state.playerMoney < UPGRADE_JUMP_COST) return null;
  return {
    ...state,
    playerMoney: state.playerMoney - UPGRADE_JUMP_COST,
    roster: upgradeUnitJump(state.roster, unitId),
  };
}

// =============================================================================
// LOADOUT BUILDING
// =============================================================================

export function buildCampaignLoadout(inventory: CampaignInventory): PlayerLoadout {
  const ammo: Partial<Record<WeaponKey, number | null>> = {};

  // Standard is always unlimited
  ammo.standard = null;

  // Probe is always owned, uses stock count (default 0 if none purchased)
  ammo.probe = inventory.ammoStock.probe ?? 0;

  // All other weapons: owned → stock count, not owned → 0
  const allWeapons: WeaponKey[] = [
    'heavy', 'cluster', 'nuke', 'punch', 'antigrav',
    'gravBomb', 'tether', 'terraformer', 'emp', 'drill', 'fire',
  ];

  for (const w of allWeapons) {
    if (inventory.ownedWeapons.has(w)) {
      ammo[w] = inventory.ammoStock[w] ?? 0;
    } else {
      ammo[w] = 0;
    }
  }

  // Gear no longer rides on the loadout: it resolves into player.modifiers (core/unitModifiers.ts)
  return {
    ammo,
    upgrades: {},
    items: { ...inventory.items },
  };
}

// =============================================================================
// POST-BATTLE INVENTORY RETURN
// =============================================================================

export function returnUnusedInventory(
  inventory: CampaignInventory,
  preBattleLoadout: PlayerLoadout,
  postBattlePlayer: PlayerState
): CampaignInventory {
  const newAmmoStock = { ...inventory.ammoStock };
  const newItems = { ...inventory.items };

  // Return unused ammo for each weapon
  for (const [key, preCount] of Object.entries(preBattleLoadout.ammo)) {
    const weapon = key as WeaponKey;
    if (preCount === null || preCount === undefined) continue; // unlimited, skip
    if (weapon === 'standard') continue; // unlimited

    const postCount = postBattlePlayer.loadout.ammo[weapon];
    const remaining = typeof postCount === 'number' ? postCount : 0;

    if (remaining > 0) {
      newAmmoStock[weapon] = (newAmmoStock[weapon] ?? 0) + remaining;
    }
  }

  // Return unused items
  if (preBattleLoadout.items) {
    for (const [key, preCount] of Object.entries(preBattleLoadout.items)) {
      const item = key as ItemKey;
      if (!preCount) continue;

      const postCount = postBattlePlayer.loadout.items?.[item] ?? 0;
      if (postCount > 0) {
        newItems[item] = (newItems[item] ?? 0) + postCount;
      }
    }
  }

  return {
    ...inventory,
    ammoStock: newAmmoStock,
    items: newItems,
  };
}
